import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Select,
  Input,
  Form,
  Button,
  notification,
  Row,
  Col,
  Alert,
  Modal,
  Table,
  Space,
  Popconfirm,
  Typography,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { setSideTab } from "../../../toolkit/slices/inventories/inventorySlice";
import accessCheckPromise from "../../../../utils/accessCheck";
import { logoutUser } from "../../../toolkit/slices/users/userSlice";
import AuthorizationError from "../AuthorizationError";
import { COMPANY_CATEGORIES } from "../../../../utils/companyCategories";
const { Option } = Select;
const { Title } = Typography;

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
const DEBOUNCE_DELAY = 400;
const EMAIL_DISPLAY_MAX_LENGTH = 18;
// simple debounce factory — each call returns a function with its own timer
const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};
// const checkGstPanApi = async (gstNumber, panNumber) => {
//   try {
//     const params = new URLSearchParams();
//     if (gstNumber) params.append("gstNumber", gstNumber);
//     if (panNumber) params.append("panNumber", panNumber);
//     const res = await fetch(
//       `${apiBaseUrl}/inventoryManagement/getCheckPanAndGst?${params.toString()}`,
//       { method: "GET", credentials: "include" },
//     );
//     if (res.ok) return { ok: true };
//     const data = await res.json().catch(() => ({}));
//     return {
//       ok: false,
//       message: data.message || "Already exists",
//       name: Array.isArray(data.name) ? data.name : data.name ? [data.name] : [],
//     };
//   } catch (error) {
//     console.error("Error checking GST/PAN:", error);
//     return { ok: true };
//   }
// };

const checkGstPanApi = async (gstNumber, panNumber) => {
  try {
    const params = new URLSearchParams();
    if (gstNumber) params.append("gstNumber", gstNumber);
    if (panNumber) params.append("panNumber", panNumber);

    const res = await fetch(
      `${apiBaseUrl}/inventoryManagement/getCheckPanAndGst?${params.toString()}`,
      { method: "GET", credentials: "include" },
    );

    if (res.ok) return { ok: true };

    const data = await res.json().catch(() => ({}));

    const names = Array.isArray(data.names)
      ? data.names
      : data.name
        ? [data.name]
        : [];

    return { ok: false, names };
  } catch (error) {
    console.error("Error checking GST/PAN:", error);
    return { ok: true };
  }
};

const ClientManagementContainer = () => {
  const [access, setAccess] = useState(false);
  const [cities, setCities] = useState([]);
  const [states, setStates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [archivingId, setArchivingId] = useState(null);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setSideTab(window.location.pathname));
    accessCheckPromise()
      .then((res) => setAccess(res))
      .catch(() => {
        dispatch(logoutUser());
      });
  }, []);

  const fetchCompanyData = useCallback(
    async ({ page = 1, limit = 20, search = "" } = {}) => {
      setCompaniesLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        });
        if (search.trim()) {
          params.append("search", search.trim());
        }

        const response = await fetch(
          `${apiBaseUrl}/inventoryManagement/listPartyCompanies?${params.toString()}`,
          {
            method: "GET",
            credentials: "include",
          },
        );
        const data = await response.json();

        if (data.success && Array.isArray(data.data)) {
          setCompanies(data.data);
          setPagination((prev) => ({
            ...prev,
            current: data.pagination?.page || page,
            pageSize: data.pagination?.limit || limit,
            total: data.pagination?.total ?? 0,
          }));
          return data.data;
        }
        console.error("Unexpected response format:", data);
        return null;
      } catch (error) {
        console.error("Error fetching company data", error);
        return null;
      } finally {
        setCompaniesLoading(false);
      }
    },
    [],
  );

  const refreshCompanies = useCallback(() => {
    return fetchCompanyData({
      page: pagination.current,
      limit: pagination.pageSize,
      search: searchText,
    });
  }, [fetchCompanyData, pagination.current, pagination.pageSize, searchText]);

  useEffect(() => {
    if (!access) return;

    const timer = setTimeout(() => {
      fetchCompanyData({
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
      });
    }, searchText.trim() ? DEBOUNCE_DELAY : 0);

    return () => clearTimeout(timer);
  }, [
    access,
    pagination.current,
    pagination.pageSize,
    searchText,
    fetchCompanyData,
  ]);

  // Fetch cities data when component mounts
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await fetch(
          "https://raw.githubusercontent.com/nshntarora/Indian-Cities-JSON/master/cities.json",
        );
        const citiesData = await response.json();
        setCities(citiesData);

        // Extract unique states
        const uniqueStates = [
          ...new Set(citiesData.map((city) => city.state)),
        ].sort();
        setStates(uniqueStates);
      } catch (error) {
        console.error("Error fetching cities:", error);
      }
    };

    fetchCities();
  }, []);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize,
    }));
  };

  const handleOpenEdit = (company) => {
    setEditingCompany(company);
    setEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setEditModalOpen(false);
    setEditingCompany(null);
  };

  const handleArchiveClient = async (companyId) => {
    try {
      setArchivingId(companyId);
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/archiveClient`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ id: companyId }),
        },
      );

      const data = await response.json();
      if (data.success) {
        notification.success({ message: data.message });
        await refreshCompanies();
      } else {
        notification.error({ message: data.message || "Failed to archive company" });
      }
    } catch (error) {
      notification.error({
        message: "Failed to archive company. Please try again.",
      });
    } finally {
      setArchivingId(null);
    }
  };

  const isEmptyCell = (text) =>
    text === null || text === undefined || text === "";

  const renderEmpty = () => (
    <span style={{ display: "block", textAlign: "center" }}>-</span>
  );

  const renderCell = (text) => {
    if (text === 0) return <span title="0">0</span>;
    if (isEmptyCell(text)) return renderEmpty();
    const value = String(text);
    return <span title={value}>{value}</span>;
  };

  const renderCenterCell = (text) => {
    if (text === 0) {
      return (
        <span style={{ display: "block", textAlign: "center" }} title="0">
          0
        </span>
      );
    }
    if (isEmptyCell(text)) return renderEmpty();
    const value = String(text);
    return (
      <span style={{ display: "block", textAlign: "center" }} title={value}>
        {value}
      </span>
    );
  };

  const renderEmailCell = (text) => {
    if (isEmptyCell(text)) return renderEmpty();
    const value = String(text);
    const isTruncated = value.length > EMAIL_DISPLAY_MAX_LENGTH;
    const display = isTruncated
      ? `${value.slice(0, EMAIL_DISPLAY_MAX_LENGTH)}...`
      : value;

    const content = <span style={{ cursor: isTruncated ? "pointer" : "default" }}>{display}</span>;

    if (!isTruncated) return content;

    return (
      <Tooltip
        title={value}
        placement="topLeft"
        mouseEnterDelay={0.2}
        overlayStyle={{ maxWidth: 360 }}
      >
        {content}
      </Tooltip>
    );
  };

  const components = {
    header: {
      cell: (props) => (
        <th
          {...props}
          style={{
            ...props.style,
            backgroundColor: "#daeffe",
            fontSize: "12px",
            fontWeight: "bold",
            textAlign: "center",
          }}
        />
      ),
    },
  };

  const columns = [
    {
      title: "Company Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      fixed: "left",
      render: renderCell,
    },
    {
      title: "GST Number",
      dataIndex: "gstNumber",
      key: "gstNumber",
      width: 150,
      align: "center",
      render: renderCenterCell,
    },
    {
      title: "PAN Number",
      dataIndex: "panNumber",
      key: "panNumber",
      width: 120,
      align: "center",
      render: renderCenterCell,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 180,
      render: renderEmailCell,
    },
    {
      title: "Contact Number",
      dataIndex: "contactNumber",
      key: "contactNumber",
      width: 130,
      align: "center",
      render: renderCenterCell,
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 110,
      align: "center",
      render: renderCenterCell,
    },
    {
      title: "Contact Person",
      dataIndex: "contactPerson",
      key: "contactPerson",
      width: 140,
      align: "center",
      render: renderCenterCell,
    },
    {
      title: "Address 1",
      dataIndex: "add1",
      key: "add1",
      width: 160,
      ellipsis: true,
      render: renderCell,
    },
    {
      title: "Address 2",
      dataIndex: "add2",
      key: "add2",
      width: 160,
      ellipsis: true,
      render: renderCell,
    },
    {
      title: "Address 3",
      dataIndex: "add3",
      key: "add3",
      width: 160,
      ellipsis: true,
      render: renderCell,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      width: 150,
      align: "center",
      render: renderCenterCell,
    },
    {
      title: "Pincode",
      dataIndex: "pinCode",
      key: "pinCode",
      width: 100,
      align: "center",
      render: renderCenterCell,
    },
    {
      title: "State",
      dataIndex: "state",
      key: "state",
      width: 120,
      align: "center",
      render: renderCenterCell,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleOpenEdit(record)}
            title="Edit"
          />
          <Popconfirm
            title="Archive this company?"
            description="This will hide the company from active lists. Historical records will be preserved."
            onConfirm={() => handleArchiveClient(record._id)}
            okText="Archive"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              loading={archivingId === record._id}
              title="Archive"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!access) {
    return <AuthorizationError />;
  }

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "20px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "1rem",
          marginBottom: "1rem",
          flexWrap: "wrap",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Client Management
        </Title>
        <Space wrap>
          <Input
            allowClear
            placeholder="Search companies..."
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearchChange}
            style={{ width: 280 }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setAddModalOpen(true)}
          >
            Add Company
          </Button>
        </Space>
      </div>

      <Table
        rowKey="_id"
        columns={columns}
        dataSource={companies}
        loading={companiesLoading}
        components={components}
        scroll={{ x: 2100 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `${total} companies`,
          onChange: handleTableChange,
        }}
      />

      <Modal
        title="Add Company"
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        footer={null}
        width={960}
        destroyOnClose
      >
        <CompanyAddForm
          cities={cities}
          states={states}
          setCities={setCities}
          onSuccess={async () => {
            setAddModalOpen(false);
            await refreshCompanies();
          }}
        />
      </Modal>

      <Modal
        title="Edit Company"
        open={editModalOpen}
        onCancel={handleCloseEdit}
        footer={null}
        width={960}
        destroyOnClose
      >
        {editingCompany ? (
          <CompanyEditForm
            cities={cities}
            states={states}
            company={editingCompany}
            onSuccess={async () => {
              handleCloseEdit();
              await refreshCompanies();
            }}
          />
        ) : null}
      </Modal>
    </div>
  );
};

export default ClientManagementContainer;

const CompanyAddForm = ({ cities, states, setCities, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [gstAlert, setGstAlert] = useState(null);
  const [panAlert, setPanAlert] = useState(null);
  const [addCityOpen, setAddCityOpen] = useState(false);
  const [manualCityName, setManualCityName] = useState("");
  const [manualCityState, setManualCityState] = useState("");
  const citySelectRef = useRef(null);

  const debouncedCheckGst = useRef(
    debounce(async (val) => {
      if (!val) {
        form.setFields([{ name: "gstNumber", errors: [] }]);
        setGstAlert(null);
        return;
      }

      const res = await checkGstPanApi(val, undefined);

      if (!res.ok) {
        form.setFields([{ name: "gstNumber", errors: [res.message] }]);
        setGstAlert({ message: res.message, names: res.names || [] });
      } else {
        form.setFields([{ name: "gstNumber", errors: [] }]);
        setGstAlert(null);
      }
    }, DEBOUNCE_DELAY),
  );

  const debouncedCheckPan = useRef(
    debounce(async (val) => {
      if (!val) {
        form.setFields([{ name: "panNumber", errors: [] }]);
        setPanAlert(null);
        return;
      }

      const res = await checkGstPanApi(undefined, val);

      if (!res.ok) {
        form.setFields([{ name: "panNumber", errors: [res.message] }]);
        setPanAlert({ message: res.message, names: res.names || [] });
      } else {
        form.setFields([{ name: "panNumber", errors: [] }]);
        setPanAlert(null);
      }
    }, DEBOUNCE_DELAY),
  );
  const openAddCity = () => {
    setAddCityOpen(true);
    // try to focus select after closing modal later
  };

  const closeAddCity = () => {
    setAddCityOpen(false);
    setManualCityName("");
    setManualCityState("");
    // focus city select after modal closes
    setTimeout(() => {
      // AntD Select doesn't always expose focus from ref reliably,
      // so we use querySelector fallback
      const el = document.querySelector("#city-select .ant-select-selector");
      if (el) el.click();
    }, 150);
  };

  const saveManualCity = () => {
    const name = manualCityName.trim();
    const state = manualCityState.trim();

    if (!name || !state) {
      notification.warning({ message: "Enter both City and State" });
      return;
    }

    // prevent duplicates by city+state
    const exists = cities.some(
      (c) =>
        String(c?.name || "").toLowerCase() === name.toLowerCase() &&
        String(c?.state || "").toLowerCase() === state.toLowerCase(),
    );

    if (!exists) {
      const newCity = {
        id: `manual-${Date.now()}`,
        name,
        state,
      };
      setCities((prev) => [newCity, ...prev]);
    }

    // select it in the form + auto-set state
    form.setFieldsValue({ city: name, state });

    setAddCityOpen(false);
    setManualCityName("");
    setManualCityState("");
  };

  // Handle city selection to auto-populate state
  const handleCityChange = (cityName) => {
    if (cityName) {
      const selectedCity = cities.find((city) => city.name === cityName);
      if (selectedCity) {
        form.setFieldsValue({ state: selectedCity.state });
      }
    } else {
      // Clear state when city is cleared
      form.setFieldsValue({ state: undefined });
    }
  };

  const handleGstChange = (e) => {
    const val = e.target.value;
    form.setFieldsValue({ gstNumber: val });
    form.setFields([{ name: "gstNumber", errors: [] }]);
    setGstAlert(null);
    debouncedCheckGst.current(val);
  };

  // called on PAN input change
  const handlePanChangeDebounced = (e) => {
    const upper = e.target.value.toUpperCase();
    form.setFieldsValue({ panNumber: upper });
    form.setFields([{ name: "panNumber", errors: [] }]);
    setPanAlert(null);
    debouncedCheckPan.current(upper);
  };

  const handleAddClient = async (info) => {
    try {
      setLoading(true);

      const trimmedInfo = {
        ...info,
        name: info.name?.trim().toUpperCase(),
      };

      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/addClient`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(trimmedInfo),
          credentials: "include",
        },
      );

      if (response.ok) {
        notification.success({
          message: "Client added successfully!",
        });
        form.resetFields();
        setGstAlert(null);
        setPanAlert(null);
        if (onSuccess) {
          await onSuccess();
        }
      } else {
        const errorData = await response.json();
        notification.error({ message: errorData.message });
      }
    } catch (error) {
      notification.error({
        message: "Failed to add client. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={handleAddClient}>
      <Row gutter={16} justify="center">
        <Col span={12}>
          <Form.Item
            label="Company Name"
            name="name"
            rules={[
              { required: true, message: "Please enter the company name" },
              {
                validator: (_, value) =>
                  value && value.trim() === ""
                    ? Promise.reject("Company name cannot be empty")
                    : Promise.resolve(),
              },
            ]}
          >
            <Input
              placeholder="Enter company name"
              style={{ textTransform: "uppercase" }}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="GST Number"
            name="gstNumber"
            rules={[
              {
                pattern:
                  /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                message: "Please enter a valid GST number",
              },
            ]}
          >
            <Input placeholder="Enter GST number" onChange={handleGstChange} />
          </Form.Item>
          {gstAlert && (
            <Alert
              showIcon
              type="warning"
              message={`Client with this GST is already associated with:- ${gstAlert.names.join(", ")}`}
              style={{ marginTop: -8, marginBottom: 12 }}
            />
          )}
        </Col>
        <Col span={12}>
          <Form.Item
            label="PAN Number"
            name="panNumber"
            rules={[
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const pan = value.toUpperCase();
                  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
                  if (!panRegex.test(pan)) {
                    return Promise.reject("Please enter a valid PAN number");
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder="Enter PAN number"
              maxLength={10}
              style={{ textTransform: "uppercase" }}
              onChange={handlePanChangeDebounced}
            />
          </Form.Item>
          {panAlert && (
            <Alert
              type="warning"
              showIcon
              message={`Client with this PAN is already associated with:- ${panAlert.names}`}
              style={{ marginTop: -8, marginBottom: 12 }}
            />
          )}
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Email"
            name="email"
            rules={[{ type: "email", message: "Please enter a valid email!" }]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Contact Number"
            name="contactNumber"
            rules={[
              {
                pattern: /^\d{10}$/,
                message: "Contact number must be exactly of 10 digits",
              },
            ]}
          >
            <Input placeholder="Enter contact number" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: "Please select a category" }]}
          >
            <Select placeholder="Select category">
              {COMPANY_CATEGORIES.map((category) => (
                <Option key={category} value={category}>
                  {category}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Contact Person"
            name="contactPerson"
            rules={[
              {
                validator: (_, value) =>
                  value && value.trim() === ""
                    ? Promise.reject("Contact person cannot be empty")
                    : Promise.resolve(),
              },
            ]}
          >
            <Input placeholder="Enter contact person name" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Address 1"
            name="add1"
            rules={[
              {
                validator: (_, value) =>
                  value && value.trim() === ""
                    ? Promise.reject("Address 1 cannot be empty")
                    : Promise.resolve(),
              },
            ]}
          >
            <Input placeholder="Enter Address 1" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Address 2"
            name="add2"
            rules={[
              {
                validator: (_, value) =>
                  value && value.trim() === ""
                    ? Promise.reject("Address 2 cannot be empty")
                    : Promise.resolve(),
              },
            ]}
          >
            <Input placeholder="Enter Address 2" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Address 3"
            name="add3"
            rules={[
              {
                validator: (_, value) =>
                  value && value.trim() === ""
                    ? Promise.reject("Address 3 cannot be empty")
                    : Promise.resolve(),
              },
            ]}
          >
            <Input placeholder="Enter Address 3" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="City"
            name="city"
            rules={[{ required: true, message: "Please select a city" }]}
          >
            <Select
              id="city-select"
              ref={citySelectRef}
              placeholder="Select city"
              showSearch
              allowClear
              filterOption={(input, option) =>
                (option?.children ?? "")
                  .toLowerCase()
                  .includes(input.trim().toLowerCase())
              }
              onChange={handleCityChange}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <div style={{ padding: 2, borderTop: "1px solid #f0f0f0" }}>
                    <Button type="link" onClick={openAddCity}>
                      + Add city manually
                    </Button>
                  </div>
                </>
              )}
            >
              {cities.map((city) => (
                <Option key={city.id} value={city.name}>
                  {city.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Modal
          title="Add City"
          open={addCityOpen}
          onCancel={closeAddCity}
          onOk={saveManualCity}
          okText="Add"
        >
          <Form layout="vertical">
            <Form.Item label="City Name" required>
              <Input
                value={manualCityName}
                onChange={(e) => setManualCityName(e.target.value)}
                placeholder="Enter city name"
              />
            </Form.Item>

            <Form.Item label="State" required>
              <Select
                value={manualCityState || undefined}
                onChange={(v) => setManualCityState(v)}
                placeholder="Select state"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  (option?.children ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              >
                {states.map((st) => (
                  <Option key={st} value={st}>
                    {st}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form>
        </Modal>

        <Col span={8}>
          <Form.Item
            label="Pincode"
            name="pinCode"
            rules={[
              {
                pattern: /^\d{6}$/,
                message: "Pincode must be exactly 6 digits",
              },
            ]}
          >
            <Input placeholder="Enter pincode" maxLength={6} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="State"
            name="state"
            rules={[{ required: true, message: "Please select a state" }]}
          >
            <Select placeholder="Select state" disabled={true}>
              {states.map((state) => (
                <Option key={state} value={state}>
                  {state}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item style={{ textAlign: "center" }}>
        <Button type="primary" htmlType="submit" loading={loading}>
          Add Company
        </Button>
      </Form.Item>
    </Form>
  );
};

const CompanyEditForm = ({ cities, states, company, onSuccess }) => {
  const [form] = Form.useForm();
  const [companyInfo, setCompanyInfo] = useState({
    id: "",
    gstNumber: "",
    panNumber: "",
    name: "",
    email: "",
    contactNumber: "",
    contactPerson: "",
    category: "",
    add1: "",
    add2: "",
    add3: "",
    city: "",
    state: "",
    pinCode: "",
  });

  useEffect(() => {
    if (!company) return;

    setCompanyInfo({
      id: company._id || "",
      gstNumber: company.gstNumber || "",
      panNumber: company.panNumber || "",
      name: company.name || "",
      email: company.email || "",
      contactNumber: company.contactNumber || "",
      contactPerson: company.contactPerson || "",
      category: company.category || "",
      add1: company.add1 || "",
      add2: company.add2 || "",
      add3: company.add3 || "",
      city: company.city || "",
      state: company.state || "",
      pinCode: company.pinCode || "",
    });
  }, [company]);

  const handleChange = (field, value) => {
    setCompanyInfo((prevInfo) => ({
      ...prevInfo,
      [field]: value,
    }));

    // Handle city selection to auto-populate state
    if (field === "city") {
      if (value) {
        const selectedCity = cities.find((city) => city.name === value);
        if (selectedCity) {
          setCompanyInfo((prevInfo) => ({
            ...prevInfo,
            [field]: value,
            state: selectedCity.state,
          }));
        }
      } else {
        // Clear state when city is cleared
        setCompanyInfo((prevInfo) => ({
          ...prevInfo,
          [field]: value,
          state: "",
        }));
      }
    }
  };

  const handleSave = async () => {
    const didSave = await editClient();
    if (didSave && onSuccess) {
      await onSuccess();
    }
  };

  const editClient = async () => {
    let reqBody = {
      id: companyInfo.id,
      gstNumber: companyInfo.gstNumber?.trim().toUpperCase(),
      panNumber: companyInfo.panNumber?.trim().toUpperCase(),
      name: companyInfo.name?.trim().toUpperCase(),
      email: companyInfo.email?.trim(),
      contactNumber: companyInfo.contactNumber,
      contactPerson: companyInfo.contactPerson?.trim(),
      category: companyInfo.category,
      add1: companyInfo.add1?.trim(),
      add2: companyInfo.add2?.trim(),
      add3: companyInfo.add3?.trim(),
      city: companyInfo.city?.trim(),
      state: companyInfo.state?.trim(),
      pinCode: companyInfo.pinCode,
    };

    try {
      const response = await fetch(
        `${apiBaseUrl}/inventoryManagement/editClient`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(reqBody),
        },
      );

      const data = await response.json();
      if (data.success) {
        notification.success({
          message: data.message,
        });
        return true;
      } else {
        notification.error({
          message: data.message,
        });
        return false;
      }
    } catch (error) {
      console.error("Error updating client", error);
      return false;
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      email: companyInfo.email,
      name: companyInfo.name,
      gstNumber: companyInfo.gstNumber,
      panNumber: companyInfo.panNumber,
      contactNumber: companyInfo.contactNumber,
      contactPerson: companyInfo.contactPerson,
      category: companyInfo.category || undefined,
      add1: companyInfo.add1,
      add2: companyInfo.add2,
      add3: companyInfo.add3,
      city: companyInfo.city,
      state: companyInfo.state,
      pinCode: companyInfo.pinCode,
    });
  }, [companyInfo, form]);

  return (
    <div>
      <Form layout="vertical" form={form} onFinish={handleSave}>
        <Row gutter={16} justify="center">
          <Col span={12}>
            <Form.Item
              label="Edit Company Name"
              name="name"
              rules={[
                { required: true, message: "Please enter the company name" },
                {
                  validator: (_, value) =>
                    value && value.trim() === ""
                      ? Promise.reject("Company name cannot be empty")
                      : Promise.resolve(),
                },
              ]}
            >
              <Input
                placeholder="Enter Company Name"
                style={{ textTransform: "uppercase" }}
                onChange={(e) => handleChange("name", e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="GST Number"
              name="gstNumber"
              rules={[
                {
                  pattern:
                    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                  message: "Please enter a valid GST number",
                },
              ]}
            >
              <Input
                placeholder="Enter GST Number"
                onChange={(e) => handleChange("gstNumber", e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="PAN Number"
              name="panNumber"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const pan = value.toUpperCase();
                    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
                    if (!panRegex.test(pan)) {
                      return Promise.reject("Please enter a valid PAN number");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                placeholder="Enter PAN number"
                maxLength={10}
                style={{ textTransform: "uppercase" }}
                value={companyInfo.panNumber}
                onChange={(e) => {
                  const upper = e.target.value.toUpperCase();
                  handleChange("panNumber", upper);
                }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Email"
              name="email"
              rules={[
                { type: "email", message: "Please enter a valid email!" },
              ]}
            >
              <Input
                placeholder="Enter Email"
                onChange={(e) => handleChange("email", e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Contact Number"
              name="contactNumber"
              rules={[
                {
                  pattern: /^\d{10}$/,
                  message: "Contact number must be exactly of 10 digits",
                },
              ]}
            >
              <Input
                placeholder="Enter Contact Number"
                onChange={(e) => handleChange("contactNumber", e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Category"
              name="category"
              rules={[{ required: true, message: "Please select a category" }]}
            >
              <Select
                placeholder="Select category"
                onChange={(value) => handleChange("category", value)}
              >
                {COMPANY_CATEGORIES.map((category) => (
                  <Option key={category} value={category}>
                    {category}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label="Contact Person"
              name="contactPerson"
              rules={[
                {
                  validator: (_, value) =>
                    value && value.trim() === ""
                      ? Promise.reject("Contact person cannot be empty")
                      : Promise.resolve(),
                },
              ]}
            >
              <Input
                placeholder="Enter Contact Person"
                onChange={(e) => handleChange("contactPerson", e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* <Form.Item
          label="Address"
          name="address"
          rules={[
            {
              validator: (_, value) =>
                value && value.trim() === ""
                  ? Promise.reject("Address cannot be empty")
                  : Promise.resolve(),
            },
          ]}
          style={{ width: "100%" }}
        >
          <Input
            placeholder="Enter Address"
            onChange={(e) => handleChange("address", e.target.value)}
          />
        </Form.Item> */}

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Address 1"
              name="add1"
              rules={[
                {
                  validator: (_, value) =>
                    value && value.trim() === ""
                      ? Promise.reject("Address 1 cannot be empty")
                      : Promise.resolve(),
                },
              ]}
            >
              <Input
                placeholder="Enter Address 1"
                onChange={(e) => handleChange("add1", e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Address 2"
              name="add2"
              rules={[
                {
                  validator: (_, value) =>
                    value && value.trim() === ""
                      ? Promise.reject("Address 2 cannot be empty")
                      : Promise.resolve(),
                },
              ]}
            >
              <Input
                placeholder="Enter Address 2"
                onChange={(e) => handleChange("add2", e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Address 3"
              name="add3"
              rules={[
                {
                  validator: (_, value) =>
                    value && value.trim() === ""
                      ? Promise.reject("Address 3 cannot be empty")
                      : Promise.resolve(),
                },
              ]}
            >
              <Input
                placeholder="Enter Address 3"
                onChange={(e) => handleChange("add3", e.target.value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="City"
              name="city"
              rules={[{ required: true, message: "Please select a city" }]}
            >
              <Select
                placeholder="Select city"
                showSearch
                allowClear
                filterOption={(input, option) =>
                  option?.children
                    .toLowerCase()
                    .includes(input.trim().toLowerCase())
                }
                value={companyInfo.city || undefined}
                onChange={(value) => handleChange("city", value)}
              >
                {cities.map((city) => (
                  <Option key={city.id} value={city.name}>
                    {city.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Pincode"
              name="pinCode"
              rules={[
                {
                  pattern: /^\d{6}$/,
                  message: "Pincode must be exactly 6 digits",
                },
              ]}
            >
              <Input
                placeholder="Enter pincode"
                maxLength={6}
                onChange={(e) => handleChange("pinCode", e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="State"
              name="state"
              rules={[{ required: true, message: "Please select a state" }]}
            >
              <Select
                placeholder="Select state"
                value={companyInfo.state || undefined}
                disabled={true}
              >
                {states.map((state) => (
                  <Option key={state} value={state}>
                    {state}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            type="primary"
            htmlType="submit"
            style={{ width: "100px" }}
            disabled={!companyInfo.id}
          >
            Save
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};
